import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  FaArrowDown,
  FaEnvelope,
  FaGlobe,
  FaMapMarkerAlt,
  FaPhone,
  FaShareAlt,
  FaUserTie,
} from "react-icons/fa";

import useScrollToTop from "../../../hooks/useScrollToTop";
import Reveal from "../../../components/ui/Reveal";

import About from "./About";
import SectionContacts from "./SectionContacts";
import LatestNews from "./LastNews";
import LatestEvents from "./LatestEvents";
import Diaporama from "./Diaporama";
import PalmaresTable from "./PalmaresTable";
import LastGallerie from "./LastGallerie";
import WeeklySchedule from "./WeeklySchedule";
import FormContact from "./FormContact";
import Footer from "./../HomePage/Footer";

import { getAllSections } from "../../../services/sectionsServices";
import { resolveMediaUrl } from "../HomePage/homeUtils";

const HERO_FALLBACK_IMAGE = "/img/hero-associations.jpg";

function getHeroImage(section) {
  return resolveMediaUrl(
    section?.header1_URL || section?.header2_URL,
    HERO_FALLBACK_IMAGE
  );
}

function SectionsPage() {
  useScrollToTop();

  const location = useLocation();

  const [section, setSection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImage, setCurrentImage] = useState("");
  const [fade, setFade] = useState(true);

  useEffect(() => {
    let mounted = true;

    async function fetchSection() {
      try {
        setLoading(true);

        const sections = await getAllSections();

        const matchedSection = Array.isArray(sections)
          ? sections.find((sec) => {
              return (
                sec.lien_section === location.pathname ||
                sec.slug === location.pathname ||
                `/associations/${sec.id}` === location.pathname ||
                `/sections/${sec.id}` === location.pathname
              );
            })
          : null;

        if (!mounted) return;

        setSection(matchedSection || null);

        if (matchedSection) {
          setCurrentImage(getHeroImage(matchedSection));
        }
      } catch (error) {
        console.error("Erreur lors de la récupération de la section:", error);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    fetchSection();

    return () => {
      mounted = false;
    };
  }, [location.pathname]);

  useEffect(() => {
    if (!section) return;

    const images = [section.header1_URL, section.header2_URL]
      .filter(Boolean)
      .map((image) => resolveMediaUrl(image, HERO_FALLBACK_IMAGE));

    if (images.length <= 1) return;

    let currentIndex = 0;

    const interval = setInterval(() => {
      setFade(false);

      setTimeout(() => {
        currentIndex = (currentIndex + 1) % images.length;
        setCurrentImage(images[currentIndex]);
        setFade(true);
      }, 450);
    }, 7000);

    return () => clearInterval(interval);
  }, [section]);

  const heroStats = useMemo(() => {
    if (!section) return [];

    return [
      {
        icon: FaUserTie,
        label: "Responsable",
        value: section.responsable_section || "À renseigner",
      },
      {
        icon: FaMapMarkerAlt,
        label: "Lieu",
        value: section.adresse_section || "Adresse à venir",
      },
      {
        icon: FaPhone,
        label: "Téléphone",
        value: section.tel_section || "Non renseigné",
      },
    ];
  }, [section]);

  const scrollToAbout = () => {
    const aboutSection = document.getElementById("about");

    if (aboutSection) {
      aboutSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  const scrollToContact = () => {
    const contactSection = document.getElementById("contact");

    if (contactSection) {
      contactSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
        <div className="h-[70px] w-full bg-slate-950" />

        <div className="flex min-h-[70vh] items-center justify-center px-4">
          <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm dark:border-white/10 dark:bg-slate-900">
            <p className="text-sm font-bold uppercase tracking-[0.25em] text-primary">
              Chargement
            </p>
            <h1 className="mt-3 text-2xl font-bold text-slate-950 dark:text-white">
              Récupération de l’association...
            </h1>
          </div>
        </div>
      </div>
    );
  }

  if (!section) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
        <div className="h-[70px] w-full bg-slate-950" />

        <div className="flex min-h-[70vh] items-center justify-center px-4">
          <div className="max-w-xl rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm dark:border-white/10 dark:bg-slate-900">
            <p className="text-sm font-bold uppercase tracking-[0.25em] text-primary">
              Association introuvable
            </p>
            <h1 className="mt-3 text-3xl font-bold text-slate-950 dark:text-white">
              Cette page n’existe pas ou n’est plus active.
            </h1>
            <p className="mt-4 text-slate-500 dark:text-slate-300">
              Vérifie le lien de la fiche association ou retourne sur le portail.
            </p>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-white">
      <div className="h-[70px] w-full bg-slate-950" />

      <main>
        <section className="relative min-h-[92vh] overflow-hidden bg-slate-950">
          <div
            className={[
              "absolute inset-0 bg-cover bg-center transition-all duration-700",
              fade ? "opacity-100 scale-105" : "opacity-0 scale-100",
            ].join(" ")}
            style={{
              backgroundImage: `url('${currentImage || HERO_FALLBACK_IMAGE}')`,
            }}
          />

          <div className="absolute inset-0" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/50 to-slate-950/50" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-slate-950/40" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_20%,rgba(34,197,94,0.16),transparent_36%),radial-gradient(circle_at_78%_30%,rgba(59,130,246,0.18),transparent_40%)]" />

          <div className="relative z-10 mx-auto flex min-h-[92vh] max-w-7xl flex-col justify-center px-4 py-12 sm:px-6 lg:px-8">
            <div className="grid items-center gap-10 lg:grid-cols-[1.08fr_0.92fr]">
              <Reveal>
                <div className="max-w-4xl">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="inline-flex rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-bold text-white/90 shadow-lg backdrop-blur">
                      {section.discipline_section || "Association"}
                    </span>

                    {section.is_featured && (
                      <span className="inline-flex rounded-full bg-primary px-4 py-2 text-sm font-bold text-white shadow">
                        Association mise en avant
                      </span>
                    )}
                  </div>

                  <h1 className="mt-6 text-4xl font-bold leading-tight tracking-tight text-white drop-shadow-2xl sm:text-5xl lg:text-6xl xl:text-7xl">
                    {section.nom_section}
                  </h1>

                  <p className="mt-6 max-w-3xl text-base leading-8 text-white/90 drop-shadow sm:text-lg lg:text-xl">
                    Retrouvez les informations pratiques, les horaires, les actualités,
                    les événements, les photos et les coordonnées de cette association.
                  </p>

                  <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                    <button
                      type="button"
                      onClick={scrollToAbout}
                      className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-7 py-3.5 text-sm font-bold text-slate-950 shadow-xl transition hover:-translate-y-0.5 hover:shadow-2xl"
                    >
                      En savoir plus
                      <FaArrowDown />
                    </button>

                    <button
                      type="button"
                      onClick={scrollToContact}
                      className="inline-flex items-center justify-center gap-2 rounded-2xl bg-primary px-7 py-3.5 text-sm font-bold text-white shadow-xl transition hover:-translate-y-0.5 hover:shadow-2xl"
                    >
                      Contacter
                      <FaEnvelope />
                    </button>
                  </div>

                  <div className="mt-6 flex flex-wrap gap-3">
                    {section.siteweb_section && (
                      <a
                        href={section.siteweb_section}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-bold text-white/85 backdrop-blur transition hover:bg-white hover:text-slate-950"
                      >
                        <FaGlobe />
                        Site web
                      </a>
                    )}

                    {section.facebook_section && (
                      <a
                        href={section.facebook_section}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-bold text-white/85 backdrop-blur transition hover:bg-white hover:text-slate-950"
                      >
                        <FaShareAlt />
                        Réseaux sociaux
                      </a>
                    )}
                  </div>
                </div>
              </Reveal>

              <Reveal delay={0.12}>
                <div className="grid gap-4">
                  {section.logo_section && (
                    <div className="rounded-[2rem] border border-white/15 bg-white/10 p-6 shadow-2xl backdrop-blur-md">
                      <div className="mx-auto flex h-32 w-32 items-center justify-center overflow-hidden rounded-[2rem] bg-white p-4 shadow-xl">
                        <img
                          src={resolveMediaUrl(section.logo_section)}
                          alt={`Logo ${section.nom_section}`}
                          className="h-full w-full object-contain"
                        />
                      </div>
                    </div>
                  )}

                  <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-1">
                    {heroStats.map((stat) => (
                      <HeroInfoCard
                        key={stat.label}
                        icon={stat.icon}
                        label={stat.label}
                        value={stat.value}
                      />
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        <About section={section} />
        <SectionContacts section={section} />
        <WeeklySchedule sectionId={section.id} />
        <Diaporama section={section} />
        <LatestNews sectionId={section.id} />
        <LatestEvents sectionId={section.id} />
        <PalmaresTable sectionId={section.id} />
        <LastGallerie sectionId={section.id} />
        <FormContact section={section} />
        <Footer />
      </main>
    </div>
  );
}

function HeroInfoCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-3xl border border-white/15 bg-white/10 p-5 shadow-2xl backdrop-blur-md">
      <div className="flex items-start gap-4">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-primary shadow">
          <Icon />
        </span>

        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-wide text-white/55">
            {label}
          </p>
          <p className="mt-1 line-clamp-2 text-sm font-bold leading-6 text-white">
            {value}
          </p>
        </div>
      </div>
    </div>
  );
}

export default SectionsPage;